import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  forgotPassword,
  resetPassword,
} from "../../store/slices/auth/asyncThunks";
import {
  selectForgotPasswordPending,
  selectResetPasswordPending,
  clearError,
} from "../../store/slices/auth/slice";
import {
  FaLock,
  FaEnvelope,
  FaCheck,
  FaExclamationTriangle,
  FaInfoCircle,
  FaShieldAlt,
  FaEyeSlash,
  FaEye,
} from "react-icons/fa";
import {
  Container,
  ResetHeader,
  ResetContent,
  ResetContainer,
  StepIndicator,
  Step,
  SectionTitle,
  FormGroup,
  Input,
  ErrorMessage,
  SuccessMessage,
  SubmitButton,
  InfoBox,
  PasswordGroup,
  PasswordToggle,
} from "./PasswordResetPage.styles";
import MainContainer from "../../components/MainContainer/MainContainer";
import SEO from "../../components/SEO/SEO";

const PasswordResetPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isForgotPasswordLoading = useSelector(selectForgotPasswordPending);
  const isResetPasswordLoading = useSelector(selectResetPasswordPending);

  const [currentStep, setCurrentStep] = useState(1); // 1: Email, 3: New Password
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState(""); // Full token from URL
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Check for token in URL on component mount
  useEffect(() => {
    const urlToken = searchParams.get("token");
    if (urlToken) {
      setResetToken(urlToken);
      setCurrentStep(3); // Skip to password reset step
      toast.info("Token je verifikovan, molimo unesite novu lozinku");
    }
  }, [searchParams]);

  // Handle email submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    dispatch(clearError());

    // Validate email
    if (!email.trim()) {
      setErrors({ email: "Email je obavezan" });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrors({ email: "Neispravna email adresa" });
      return;
    }

    try {
      await dispatch(forgotPassword({ email })).unwrap();

      toast.success("Link za resetovanje je poslat na vašu email adresu");
      setSuccessMessage(
        "Proverite vašu email adresu za link za resetovanje lozinke"
      );
      // Don't set step to 2, user needs to click email link
    } catch (error) {
      setErrors({
        email:
          error.message || "Greška prilikom slanja linka. Pokušajte ponovo.",
      });
      toast.error("Greška prilikom slanja linka");
    }
  };

  // Handle password reset
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setErrors({});
    dispatch(clearError());

    // Validate passwords
    if (!newPassword.trim()) {
      setErrors({ newPassword: "Nova lozinka je obavezna" });
      return;
    }

    if (newPassword.length < 8) {
      setErrors({ newPassword: "Lozinka mora imati najmanje 8 karaktera" });
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors({ confirmPassword: "Lozinke se ne poklapaju" });
      return;
    }

    // Use the full reset token from URL
    const tokenToUse = resetToken;

    if (!tokenToUse) {
      setErrors({
        general:
          "Token za resetovanje nije pronađen. Molimo pokušajte ponovo preko email linka.",
      });
      return;
    }

    try {
      await dispatch(
        resetPassword({ token: tokenToUse, password: newPassword })
      ).unwrap();

      setNewPassword("");
      setConfirmPassword("");
      toast.success("Lozinka je uspešno promenjena!");

      // Redirect to login after success
      setTimeout(() => {
        navigate("/pristup");
      }, 300);
    } catch (error) {
      setErrors({
        newPassword:
          error.message || "Greška prilikom promene lozinke. Pokušajte ponovo.",
      });
      toast.error("Greška prilikom promene lozinke");
    }
  };

  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <MainContainer>
      <SEO title="Resetovanje lozinke" path="/resetovanje-lozinke" noindex />

      <Container>
        <ResetHeader>
          <div className="reset-icon">
            <FaLock />
          </div>
          <h1>Resetovanje Lozinke</h1>
          <p>
            {currentStep === 1 &&
              "Unesite vašu email adresu da biste dobili link za resetovanje"}
            {currentStep === 3 && "Unesite vašu novu lozinku"}
          </p>
        </ResetHeader>

        <ResetContent>
          <ResetContainer>
            {/* Step Indicator */}
            <StepIndicator>
              <Step $completed={currentStep > 1} $active={currentStep === 1}>
                <FaEnvelope />
              </Step>
              <Step $active={currentStep === 3}>
                <FaShieldAlt />
              </Step>
            </StepIndicator>

            {/* Success Message */}
            {successMessage && (
              <SuccessMessage>
                <FaCheck className="icon" />
                {successMessage}
              </SuccessMessage>
            )}

            {/* Step 1: Email Input */}
            {currentStep === 1 && (
              <form onSubmit={handleEmailSubmit}>
                <SectionTitle>Unesite Email Adresu</SectionTitle>

                <InfoBox>
                  <div className="info-icon">
                    <FaInfoCircle />
                  </div>
                  <h4>Kako funkcioniše?</h4>
                  <p>
                    Poslać emo vam link na email adresu povezanu sa vašim
                    nalogom. Link važi 1 sat.
                  </p>
                </InfoBox>

                <FormGroup>
                  <label htmlFor="email">
                    Email Adresa <span className="required">*</span>
                  </label>
                  <Input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors({ ...errors, email: "" });
                    }}
                    placeholder="vasa.email@adresa.com"
                    className={errors.email ? "error" : ""}
                    required
                  />
                  {errors.email && (
                    <ErrorMessage>
                      <FaExclamationTriangle className="icon" />
                      {errors.email}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <SubmitButton type="submit" disabled={isForgotPasswordLoading}>
                  {isForgotPasswordLoading ? (
                    <>
                      <l-line-spinner
                        size="24"
                        stroke="2"
                        speed="1"
                        color="#fff"
                      />
                      Šalje se...
                    </>
                  ) : (
                    <>
                      <FaEnvelope />
                      Pošalji Link
                    </>
                  )}
                </SubmitButton>
              </form>
            )}

            {/* Step 3: New Password */}
            {currentStep === 3 && (
              <form onSubmit={handlePasswordReset}>
                <SectionTitle>Nova Lozinka</SectionTitle>

                {resetToken && (
                  <InfoBox>
                    <div className="info-icon">
                      <FaCheck />
                    </div>
                    <h4>Token Verifikovan</h4>
                    <p>
                      Vaš token je uspešno verifikovan. Molimo unesite novu
                      lozinku.
                    </p>
                  </InfoBox>
                )}

                {!resetToken && (
                  <InfoBox>
                    <div className="info-icon">
                      <FaShieldAlt />
                    </div>
                    <h4>Sigurna Lozinka</h4>
                    <p>
                      Koristite kombinaciju velikih i malih slova, brojeva i
                      specijalnih karaktera za maksimalnu bezbednost.
                    </p>
                  </InfoBox>
                )}

                {errors.general && (
                  <ErrorMessage>
                    <FaExclamationTriangle className="icon" />
                    {errors.general}
                  </ErrorMessage>
                )}

                <FormGroup>
                  <label htmlFor="newPassword">
                    Nova Lozinka <span className="required">*</span>
                  </label>
                  <PasswordGroup>
                    <Input
                      type={showPassword.newPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (errors.newPassword)
                          setErrors({ ...errors, newPassword: "" });
                      }}
                      placeholder="Minimum 8 karaktera"
                      className={errors.newPassword ? "error" : ""}
                      disabled={isResetPasswordLoading}
                      required
                    />
                    <PasswordToggle
                      type="button"
                      onClick={() => togglePassword("newPassword")}
                    >
                      {showPassword.newPassword ? <FaEyeSlash /> : <FaEye />}
                    </PasswordToggle>
                  </PasswordGroup>
                  {errors.newPassword && (
                    <ErrorMessage>
                      <FaExclamationTriangle className="icon" />
                      {errors.newPassword}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="confirmPassword">
                    Potvrdite Lozinku <span className="required">*</span>
                  </label>
                  <PasswordGroup>
                    <Input
                      type={showPassword.confirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (errors.confirmPassword)
                          setErrors({ ...errors, confirmPassword: "" });
                      }}
                      placeholder="Ponovite novu lozinku"
                      className={errors.confirmPassword ? "error" : ""}
                      required
                      disabled={isResetPasswordLoading}
                    />
                    <PasswordToggle
                      type="button"
                      onClick={() => togglePassword("confirmPassword")}
                    >
                      {showPassword.confirmPassword ? (
                        <FaEyeSlash />
                      ) : (
                        <FaEye />
                      )}
                    </PasswordToggle>
                  </PasswordGroup>

                  {errors.confirmPassword && (
                    <ErrorMessage>
                      <FaExclamationTriangle className="icon" />
                      {errors.confirmPassword}
                    </ErrorMessage>
                  )}
                </FormGroup>

                <SubmitButton type="submit" disabled={isResetPasswordLoading}>
                  {isResetPasswordLoading ? (
                    <>
                      <l-line-spinner
                        size="24"
                        stroke="2"
                        speed="1"
                        color="#fff"
                      />
                      Menja se...
                    </>
                  ) : (
                    <>
                      <FaCheck />
                      Promeni Lozinku
                    </>
                  )}
                </SubmitButton>
              </form>
            )}
          </ResetContainer>
        </ResetContent>
      </Container>
    </MainContainer>
  );
};

export default PasswordResetPage;
